import { useEffect, useMemo, useRef, useState } from "react";
import { Camera, Plus, X, Globe, Twitter, Instagram, Linkedin, BookOpen, Move } from "lucide-react";
import { SiteShell } from "@/components/layout/SiteShell";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { TID } from "@/constants/testIds";
import { toast } from "sonner";
import CertifiedBadge from "@/components/CertifiedBadge";
import EarlyAccessLimitedBanner from "@/components/EarlyAccessLimitedBanner";
import ProfileReviewsSection from "@/components/profile/ProfileReviewsSection";

const ROLE_LABELS = { author: "Author", beta_reader: "Beta Reader" };
const AVAIL = [
  { value: "open", label: "Open to new projects" },
  { value: "limited", label: "Limited availability" },
  { value: "closed", label: "Not accepting projects" },
];

export default function ProfilePage() {
  const { user, updateProfile } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [uploading, setUploading] = useState({ avatar: false, banner: false });
  const bannerDrag = useRef({ startY: 0, startPos: 50, height: 1, pending: false, active: false });
  const [draggingBanner, setDraggingBanner] = useState(false);

  useEffect(() => {
    if (user && typeof user === "object") {
      setForm({
        profile: { ...user.profile, social_links: { ...(user.profile?.social_links || {}) } },
        roles: user.roles || [],
      });
    }
  }, [user]);

  if (!form) return <SiteShell><div className="container-page py-16">Loading…</div></SiteShell>;

  const set = (path, value) => {
    setForm((f) => {
      const next = { ...f, profile: { ...f.profile } };
      if (path.startsWith("social.")) {
        next.profile.social_links = { ...next.profile.social_links, [path.slice(7)]: value };
      } else {
        next.profile[path] = value;
      }
      return next;
    });
  };

  const toggleRole = (r) => {
    setForm((f) => {
      const roles = f.roles.includes(r) ? f.roles.filter((x) => x !== r) : [...f.roles, r];
      return { ...f, roles: roles.length ? roles : f.roles };
    });
  };

  const uploadImage = async (kind, file) => {
    setUploading((u) => ({ ...u, [kind]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const url = `${BACKEND_URL}${data.url}`;
      if (kind === "avatar") {
        set("avatar_url", url);
      } else {
        // Reset position to centred whenever a new banner is uploaded so
        // the user starts from a clean state before dragging.
        setForm((f) => ({
          ...f,
          profile: { ...f.profile, banner_url: url, banner_position_y: 50 },
        }));
      }
      toast.success(kind === "avatar" ? "Profile photo updated" : "Banner updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading((u) => ({ ...u, [kind]: false }));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateProfile({ profile: form.profile, roles: form.roles });
      toast.success("Profile saved");
      setEditing(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const onBannerPointerDown = (e) => {
    if (!editing || !form.profile.banner_url) return;
    // Ignore synthetic events originating from the "Change banner" label
    // or the size-hint chip — they call stopPropagation but on some iOS
    // Safari builds the parent frame still receives the pointerdown for a
    // brief moment. Skip if the target is inside a button/label/input.
    const target = e.target;
    if (target && target.closest && target.closest("label, button, input")) return;
    const rect = e.currentTarget.getBoundingClientRect();
    bannerDrag.current = {
      startY: e.clientY,
      startPos: Number(form.profile.banner_position_y ?? 50),
      height: rect.height || 1,
      // pending = pointer is down but hasn't crossed the movement threshold
      // yet, so a straight tap does NOT hijack the touch stream. Only after
      // moving > 6px do we call setPointerCapture and start updating the
      // object-position — this is what makes the mobile UX feel idiomatic
      // and lets the file-picker label receive taps normally.
      pending: true,
      active: false,
      pointerId: e.pointerId,
    };
    setDraggingBanner(false);
  };
  const onBannerPointerMove = (e) => {
    const d = bannerDrag.current;
    if (!d.pending && !d.active) return;
    const { startY, startPos, height } = d;
    const raw = e.clientY - startY;
    // First real move → check threshold
    if (d.pending) {
      if (Math.abs(raw) < 6) return;
      d.pending = false;
      d.active = true;
      setDraggingBanner(true);
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
    }
    // Dragging DOWN reveals more of the image top → object-position-y ↓.
    const deltaPct = -(raw / height) * 100;
    const next = Math.min(100, Math.max(0, startPos + deltaPct));
    set("banner_position_y", next);
  };
  const onBannerPointerUp = (e) => {
    const d = bannerDrag.current;
    if (!d.pending && !d.active) return;
    if (d.active) {
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* noop */ }
    }
    bannerDrag.current = { startY: 0, startPos: 0, height: 1, pending: false, active: false };
    setDraggingBanner(false);
  };

  const cancel = () => {
    setForm({
      profile: { ...user.profile, social_links: { ...(user.profile?.social_links || {}) } },
      roles: user.roles || [],
    });
    setEditing(false);
  };

  return (
    <SiteShell>
      <div data-testid={TID.profile.root}>
        {/* Banner */}
        <div className="relative">
          <div
            className={`h-56 md:h-72 w-full overflow-hidden bg-[hsl(var(--sb-moss))] select-none ${
              editing && form.profile.banner_url
                ? draggingBanner
                  ? "cursor-grabbing touch-none"
                  : "cursor-grab touch-none"
                : ""
            }`}
            // touch-none is applied ONLY when editing + a banner is set so
            // vertical drag repositions the image instead of scrolling the
            // page. It's on the frame element itself; the sibling "Change
            // banner" label lives OUTSIDE this element in the DOM, so its
            // native tap → file-picker chain is not affected on Android
            // Chrome or iOS Safari.
            onPointerDown={onBannerPointerDown}
            onPointerMove={onBannerPointerMove}
            onPointerUp={onBannerPointerUp}
            onPointerCancel={onBannerPointerUp}
            data-testid="profile-banner-frame"
          >
            {form.profile.banner_url ? (
              <img
                src={form.profile.banner_url}
                alt="Banner"
                draggable={false}
                className="w-full h-full object-cover pointer-events-none"
                style={{ objectPosition: `50% ${Number(form.profile.banner_position_y ?? 50)}%` }}
                data-testid={TID.profile.bannerImage}
              />
            ) : (
              <div className="w-full h-full paper-texture" />
            )}
          </div>
          {editing && form.profile.banner_url && (
            <div
              className="pointer-events-none absolute inset-x-0 top-3 flex justify-center"
              data-testid="profile-banner-drag-hint"
            >
              <div className="inline-flex items-center gap-1.5 rounded-full bg-foreground/70 text-background text-[11px] font-medium px-3 py-1 backdrop-blur-sm">
                <Move size={12} /> Drag banner to reposition
              </div>
            </div>
          )}
          {editing && (
            // ── ANDROID CHROME MOBILE TAP FIX (Phase 39, 2026-08-26) ──
            // The content container below the banner uses `-mt-16` and
            // `position: relative`, which lifts it 64px over the bottom
            // of this banner frame AND paints it above the label (later
            // sibling in DOM, both stacking context auto → later wins).
            // Result: the label was visually rendered but every tap at
            // its position hit the invisible transparent container ABOVE
            // it, so the native label→input file-picker chain never
            // fired. Verified with document.elementFromPoint on the
            // center of the label — returned the below-banner container,
            // not the label. Adding `z-10` lifts the label above the
            // overlapping container so real taps reach it.
            //
            // The Phase 38 label→input pattern is preserved (no React
            // onClick, no programmatic .click()) and the input still
            // uses `sr-only` (Phase 39 also fixed a display:none Android
            // Chrome quirk found via the web-search consult).
            <label
              className="absolute right-4 bottom-4 z-10 btn-primary cursor-pointer"
              data-testid="profile-banner-change-btn"
            >
              <Camera size={16} /> {uploading.banner ? "Uploading…" : "Change banner"}
              <input
                type="file"
                accept="image/*"
                className="sr-only"
                data-testid={TID.profile.bannerUpload}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  // Reset so re-picking the SAME file still fires.
                  e.target.value = "";
                  if (f) uploadImage("banner", f);
                }}
              />
            </label>
          )}
          {editing && (
            <div
              className="absolute left-4 bottom-4 rounded-full bg-background/85 text-foreground/80 text-[11px] px-3 py-1 backdrop-blur-sm border border-border shadow-sm"
              // Same reason as above — this chip sits over the drag frame
              // and would otherwise start a phantom reposition on tap.
              onPointerDown={(e) => e.stopPropagation()}
              data-testid="profile-banner-size-hint"
            >
              Recommended: 1600 × 400px (4:1)
            </div>
          )}
        </div>

        <div className="container-page -mt-16 pb-24 relative">
          <EarlyAccessLimitedBanner context="profile" className="mb-6" />
          {/* Avatar overlaps banner; name/headline/roles ALWAYS render below
              the banner on both mobile and desktop. Prior layout used
              `md:flex-row md:items-end` on desktop, which placed the name
              text at the banner's baseline — on a light or gradient banner
              the display name became almost invisible. Keeping the vertical
              stack on desktop mirrors mobile and guarantees readable text
              regardless of banner colors. */}
          <div className="flex flex-col gap-4 md:gap-6">
            <div className="relative">
              <div className="w-32 h-32 md:w-40 md:h-40 rounded-full ring-4 ring-background overflow-hidden bg-[hsl(var(--sb-cream))] grid place-items-center">
                {form.profile.avatar_url ? (
                  <img src={form.profile.avatar_url} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <span className="font-serif text-4xl text-[hsl(150_32%_14%)]">
                    {(form.profile.display_name || user.email)[0]?.toUpperCase()}
                  </span>
                )}
              </div>
              {editing && (
                <label className="absolute bottom-2 right-2 rounded-full p-2.5 bg-[hsl(var(--sb-cream))] text-[hsl(150_32%_14%)] shadow-md border cursor-pointer hover:bg-[hsl(var(--sb-cream-2))]">
                  <Camera size={16} />
                  <input type="file" accept="image/*" className="hidden" data-testid={TID.profile.avatarUpload}
                    onChange={(e) => e.target.files?.[0] && uploadImage("avatar", e.target.files[0])} />
                </label>
              )}
            </div>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 md:gap-3 flex-wrap">
                  <h1 className="font-serif text-3xl md:text-4xl tracking-tight">
                    {form.profile.display_name || "Your name"}
                  </h1>
                  <CertifiedBadge
                    isCertified={user?.profile?.is_certified}
                    genres={user?.profile?.cert_genres}
                    showGenres
                  />
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {form.profile.headline || "Add a headline to introduce yourself"}
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {form.roles.map((r) => <span key={r} className="chip">{ROLE_LABELS[r] || r}</span>)}
                </div>
              </div>
              <div className="flex gap-2">
                {editing ? (
                  <>
                    <button onClick={cancel} data-testid={TID.profile.cancel} className="btn-ghost border">Cancel</button>
                    <button onClick={save} disabled={saving} data-testid={TID.profile.save} className="btn-primary">
                      {saving ? "Saving…" : "Save profile"}
                    </button>
                  </>
                ) : (
                  <button onClick={() => setEditing(true)} data-testid={TID.profile.edit} className="btn-primary">
                    Edit profile
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="mt-10 grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Section title="About">
                {editing ? (
                  <div className="space-y-4">
                    <Field label="Display name" testid={TID.profile.displayName}>
                      <input className="sb-input" value={form.profile.display_name || ""} onChange={(e) => set("display_name", e.target.value)} />
                    </Field>
                    <Field label="Headline" testid={TID.profile.headline} hint="e.g. Fantasy Author • Certified Beta Reader">
                      <input className="sb-input" value={form.profile.headline || ""} onChange={(e) => set("headline", e.target.value)} />
                    </Field>
                    <Field label="Biography" testid={TID.profile.bio}>
                      <textarea rows={5} className="sb-input" value={form.profile.bio || ""} onChange={(e) => set("bio", e.target.value)} placeholder="Tell authors and readers who you are and what you love." />
                    </Field>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label="Location" testid={TID.profile.location}>
                        <input className="sb-input" value={form.profile.location || ""} onChange={(e) => set("location", e.target.value)} />
                      </Field>
                      <Field label="Experience (years)" testid={TID.profile.experience}>
                        <input type="number" min={0} className="sb-input" value={form.profile.experience_years ?? ""} onChange={(e) => set("experience_years", e.target.value ? Number(e.target.value) : null)} />
                      </Field>
                    </div>
                    <Field label="Availability" testid={TID.profile.availability}>
                      <select className="sb-input" value={form.profile.availability || "open"} onChange={(e) => set("availability", e.target.value)}>
                        {AVAIL.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                      </select>
                    </Field>
                    <RoleEditor roles={form.roles} onToggle={toggleRole} />
                  </div>
                ) : (
                  <p className="text-sm leading-relaxed text-foreground/85 whitespace-pre-line">
                    {form.profile.bio || "Add a biography from Edit profile to introduce yourself."}
                  </p>
                )}
              </Section>

              <Section title="Genres, Styles & Languages">
                <div className="space-y-4">
                  <TagField label="Genres" values={form.profile.genres} onChange={(v) => set("genres", v)} placeholder="Add a genre (e.g. Romance, Sci-Fi)" testid={TID.profile.genres} editing={editing} />
                  <TagField label="Reading styles" values={form.profile.reading_styles} onChange={(v) => set("reading_styles", v)} placeholder="e.g. Line-by-line comments, Big-picture" testid={TID.profile.styles} editing={editing} />
                  <TagField label="Languages" values={form.profile.languages} onChange={(v) => set("languages", v)} placeholder="e.g. English (US), Spanish" testid={TID.profile.languages} editing={editing} />
                </div>
              </Section>

              <Section title="Books">
                <div className="space-y-4">
                  <TagField label="Favorite books" values={form.profile.favorite_books} onChange={(v) => set("favorite_books", v)} placeholder="Add a favorite book" testid={TID.profile.favBooks} editing={editing} />
                  <TagField label="Published books" values={form.profile.published_books} onChange={(v) => set("published_books", v)} placeholder="Add a book you've published" testid={TID.profile.pubBooks} editing={editing} />
                </div>
              </Section>
            </div>

            <aside className="space-y-6">
              <Section title="Contact & Social">
                {editing ? (
                  <div className="space-y-3">
                    <IconInput icon={Globe} placeholder="Website URL" testid={TID.profile.website} value={form.profile.social_links.website || ""} onChange={(v) => set("social.website", v)} />
                    <IconInput icon={Twitter} placeholder="Twitter / X handle or URL" testid={TID.profile.twitter} value={form.profile.social_links.twitter || ""} onChange={(v) => set("social.twitter", v)} />
                    <IconInput icon={Instagram} placeholder="Instagram" testid={TID.profile.instagram} value={form.profile.social_links.instagram || ""} onChange={(v) => set("social.instagram", v)} />
                    <IconInput icon={Linkedin} placeholder="LinkedIn" testid={TID.profile.linkedin} value={form.profile.social_links.linkedin || ""} onChange={(v) => set("social.linkedin", v)} />
                    <IconInput icon={BookOpen} placeholder="Goodreads" testid={TID.profile.goodreads} value={form.profile.social_links.goodreads || ""} onChange={(v) => set("social.goodreads", v)} />
                  </div>
                ) : (
                  <SocialsView links={form.profile.social_links} />
                )}
              </Section>

              <Section title="Portfolio">
                <p className="text-sm text-muted-foreground">Expanded portfolio features — showcase individual works, samples and covers — are on the way. For now, add your published books and links above.</p>
              </Section>
            </aside>
          </div>

          {/* Publicly-revealed reviews — same block visitors see on the
              public /profile/:userId page. Locked / unrevealed reviews
              never surface here (endpoint applies the double-blind rule).
              Only rendered when the logged-in user is loaded. */}
          {user?.id && (
            <ProfileReviewsSection
              userId={user.id}
              displayName={form.profile.display_name || user?.email}
              testIdPrefix="own-profile"
            />
          )}
        </div>
      </div>
    </SiteShell>
  );
}

function Section({ title, children }) {
  return (
    <section className="card-soft">
      <h2 className="font-serif text-xl">{title}</h2>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function Field({ label, children, hint, testid }) {
  return (
    <div>
      <label className="text-sm font-medium mb-1.5 block">{label}</label>
      <div data-testid={testid && `${testid}-wrap`}>{children}</div>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function RoleEditor({ roles, onToggle }) {
  const has = (r) => roles.includes(r);
  return (
    <div>
      <label className="text-sm font-medium mb-1.5 block">Roles</label>
      <div className="grid grid-cols-2 gap-2">
        {[["author", "Author"], ["beta_reader", "Beta Reader"]].map(([v, l]) => (
          <button key={v} type="button" onClick={() => onToggle(v)}
            className={`rounded-2xl border py-2 text-sm ${has(v) ? "border-[hsl(var(--sb-forest))] bg-[hsl(var(--sb-moss))] text-[hsl(var(--sb-forest-2))]" : "hover:bg-muted"}`}>
            {l}
          </button>
        ))}
      </div>
    </div>
  );
}

function TagField({ label, values = [], onChange, placeholder, testid, editing }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (!values.includes(v)) onChange([...values, v]);
    setDraft("");
  };
  const remove = (i) => onChange(values.filter((_, idx) => idx !== i));

  return (
    <div>
      <label className="text-sm font-medium mb-1.5 block">{label}</label>
      <div className="flex flex-wrap gap-2">
        {values.map((v, i) => (
          <span key={`${v}-${i}`} className="chip">
            {v}
            {editing && (
              <button type="button" onClick={() => remove(i)} className="text-[hsl(var(--sb-forest-2))] hover:text-destructive">
                <X size={12} />
              </button>
            )}
          </span>
        ))}
        {values.length === 0 && !editing && <span className="text-sm text-muted-foreground">Not added yet.</span>}
      </div>
      {editing && (
        <>
          <div className="mt-2 flex gap-2">
            <input
              className="sb-input flex-1"
              placeholder={placeholder}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
              data-testid={testid}
            />
            <button
              type="button"
              onClick={add}
              className="btn-primary shrink-0 whitespace-nowrap"
              aria-label={`Add ${(label || "item").toLowerCase()}`}
              data-testid={testid ? `${testid}-add` : undefined}
            >
              <Plus size={16} /> Add
            </button>
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">
            Type an item and press Enter (or click <span className="font-semibold">Add</span>) — repeat for each one.
          </p>
        </>
      )}
    </div>
  );
}

function IconInput({ icon: Icon, placeholder, value, onChange, testid }) {
  return (
    <div className="flex items-center gap-2 rounded-xl border bg-background pl-3">
      {/* Phase 43 — icons still read as "extremely dark" against the dark
          input backgrounds on real screens. Using a saturated accent that
          renders as high-visibility gold in dark mode (bright on deep
          teal) and deep forest in light mode (dark on cream), plus a
          thicker stroke for the small 18px glyph so its shape reads at
          a glance. Same layout, same box, only the symbol's own contrast
          is boosted. */}
      <Icon
        size={18}
        strokeWidth={2.75}
        className="text-[hsl(var(--sb-forest))] dark:text-[hsl(var(--sb-gold))] shrink-0"
      />
      <input className="flex-1 bg-transparent py-3 pr-3 text-sm outline-none" placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}

function SocialsView({ links = {} }) {
  const entries = Object.entries(links).filter(([, v]) => !!v);
  if (entries.length === 0) return <p className="text-sm text-muted-foreground">No links added yet.</p>;
  return (
    <ul className="space-y-2 text-sm">
      {entries.map(([k, v]) => (
        <li key={k} className="flex justify-between gap-2">
          <span className="text-muted-foreground capitalize">{k}</span>
          <a href={v.startsWith("http") ? v : `https://${v}`} target="_blank" rel="noreferrer" className="link-underline truncate max-w-[16rem]">{v}</a>
        </li>
      ))}
    </ul>
  );
}
